/** Positions that make an element the containing block of its absolute children. */
const CONTAINING_BLOCK_POSITIONS = new Set(['relative', 'absolute', 'fixed', 'sticky']);

export function isContainingBlock(element: HTMLElement): boolean {
  return CONTAINING_BLOCK_POSITIONS.has(getComputedStyle(element).position);
}

const isApplied = (value: string | undefined): boolean => Boolean(value) && value !== 'none';

/** Clips what it holds at its own edges, or transforms, filters, or fades it along with itself. */
export const clipsOrRepaints = (element: Element): boolean => {
  const style = getComputedStyle(element);
  return (
    /auto|scroll|hidden|clip/.test(`${style.overflow}${style.overflowX}${style.overflowY}`) ||
    [
      style.transform,
      style.translate,
      style.rotate,
      style.scale,
      style.perspective,
      style.filter,
      style.backdropFilter,
      style.clipPath,
      style.maskImage
    ].some(isApplied) ||
    (style.opacity !== '' && Number(style.opacity) < 1) ||
    /paint|strict|content/.test(style.contain) ||
    /transform|perspective|filter/.test(style.willChange)
  );
};

/** Viewport y to scrolled-content y: past the border, then past what's scrolled off. */
export function scrollerContentOffset(scroller: HTMLElement): number {
  return scroller.scrollTop - scroller.getBoundingClientRect().top - scroller.clientTop;
}

/**
 * Whether the element lays out a box with any size. It has none when it or an ancestor
 * is display:none, and then its zeroed rect reads as the viewport's top edge.
 */
export const hasRenderedBox = (element: Element): boolean =>
  Array.from(element.getClientRects()).some(({width, height}) => width > 0 || height > 0);

/** `element` and its ancestors, up to and including `ancestor`. */
export function selfAndAncestorsThrough(element: Element, ancestor: Element): Element[] {
  const chain: Element[] = [];
  for (let current: Element | null = element; current; current = current.parentElement) {
    chain.push(current);
    if (current === ancestor) {
      break;
    }
  }
  return chain;
}
